import { get, post } from '@partsbadger/utils';
import { action, observable, runInAction, makeObservable } from 'mobx';
import { IInvoice, IInvoiceLineItemProps, IInvoicePayability } from '../types/types';

type PaginatedInvoices = {
  count: number;
  next: string | null;
  previous: string | null;
  results: IInvoice[];
};

class InvoiceStore {
  loading = false;
  invoices: IInvoice[] = [];        // always an array for components
  invoicesCount = 0;                 //  optional helper
  invoiceData: IInvoice | null = null;
  invoiceLineItems: IInvoiceLineItemProps[] = [];

  constructor() {
    makeObservable(this, {
      loading: observable,
      invoices: observable,
      invoicesCount: observable,
      invoiceData: observable,
      invoiceLineItems: observable,
      // Actions
      getInvoiceList: action,
      getInvoiceDetails: action,
      payInvoice: action,
      getPayability: action,
      invoicePreview: action,
      getInvoiceListByOrderId: action,
      downloadAllInvoices: action,
      downloadInvoice: action,
      multipleDownloadInvoice: action,
    });
  }

  // List (paginated) — normalize to array
  getInvoiceList = async (params: any) => {
    runInAction(() => { this.loading = true; });
    const response = await get(`/customer/invoices/`, { params });

    runInAction(() => {
      this.invoices = response?.results ?? [];  // normalize
      this.invoicesCount = response?.count ?? this.invoices.length;
      this.loading = false;
    });

    return response; // callers that need .results can still use it
  };

  // Details
  getInvoiceDetails = async (id: string | number) => {
    runInAction(() => { this.loading = true; });
    const response = await get(`/customer/invoices/${id}`);

    runInAction(() => {
      this.invoiceData = response;
      this.invoiceLineItems = response.line_items;
      this.loading = false;
    });

    return response;
  };

  payInvoice = async (id: number, payload: any) => {
    runInAction(() => { this.loading = true; });
    const response = await post(`/customer/invoices/${id}/payment/`, payload);
    runInAction(() => { this.loading = false; });
    return response;
  };

  // Live Zoho preflight for the invoice detail page. The backend fails open,
  // so callers should also treat any local error as "no live balance available"
  // and fall back to total_with_shipping.
  getPayability = async (id: number): Promise<IInvoicePayability> => {
    return get(`/customer/invoices/${id}/payable/`);
  };

  invoicePreview = async (id: number) => {
    return get(`/customer/invoices/${id}/preview`, { responseType: 'blob' });
  };

  // List by order — usually returns an array; normalize anyway
  getInvoiceListByOrderId = async (id: number) => {
    runInAction(() => { this.loading = true; });
    const response = await get(`/customer/sales-orders/${id}/invoices/`);

    const list = Array.isArray(response) ? response : (response?.results ?? []);
    runInAction(() => {
      this.invoices = list;               // always array
      this.invoicesCount = list.length;
      this.loading = false;
    });

    return response;
  };

  downloadAllInvoices = async (id: number) => {
    runInAction(() => { this.loading = true; });
    const response = await get(`/customer/sales-orders/${id}/download-all-invoices/`, { responseType: 'blob' });
    runInAction(() => { this.loading = false; });
    return response;
    };

  downloadInvoice = async (id: number) => {
    runInAction(() => { this.loading = true; });
    const response = await get(`/customer/invoices/${id}/download/`, { responseType: 'blob' });
    runInAction(() => { this.loading = false; });
    return response;
  };

  multipleDownloadInvoice = async (params: any) => {
    runInAction(() => { this.loading = true; });
    const response = await get(`/customer/invoices/multiple-download/?${params}`, { responseType: 'blob' });
    runInAction(() => { this.loading = false; });
    return response;
  };
}

export default new InvoiceStore();
